/**
 * Page liste — catalogue des espèces.
 * Recherche live + filtre "espèces à éviter" + filtre par zone.
 */

import { el, clear, debounce } from '../js/utils.js';
import { t } from '../js/i18n.js';
import { listSpecies, formatSize, displayName } from '../js/species.js';
import { getZone, zoneName } from '../js/zones.js';
import { renderHeader } from '../components/header.js';
import { renderFooter } from '../components/footer.js';

const CATEGORIES = ['fish', 'mollusk', 'coral', 'crustacean', 'echinoderm', 'plant', 'mammal', 'turtle', 'other'];

export async function renderSpeciesList(root, ctx = {}) {
  clear(root);

  const zoneId = ctx.query?.zone_id ?? null;
  let selectedCategory = null;

  // Charger le nom de la zone si un filtre est actif
  let zone = null;
  if (zoneId) {
    try {
      zone = await getZone(zoneId);
    } catch (e) {
      console.warn('[species-list] zone non trouvée :', e);
    }
  }

  const zoneLabel = zone ? zoneName(zone) : null;

  const view = el(
    'main',
    { class: 'list-page' },
    el(
      'header',
      { class: 'list-header container' },
      el('p', { class: 'eyebrow', style: { color: 'var(--coral)' } }, t('list.eyebrow')),
      el('h1', { class: `list-title${zoneLabel ? ' list-title--zone' : ''}` },
        zoneLabel ?? t('list.title')
      ),
      el('p', { class: 'list-lead muted' }, t('list.lead')),
      zoneId && el('a', {
        href: `#/swipe?zone_id=${zoneId}`,
        class: 'btn btn-primary list-start-btn',
      }, t('home.cta.start'))
    ),

    el(
      'section',
      { class: 'list-toolbar container' },
      el('input', {
        type: 'search',
        class: 'input list-search',
        placeholder: t('list.search.placeholder'),
        id: 'search-input',
        autocomplete: 'off',
      }),
      el(
        'label',
        { class: 'list-toggle' },
        el('input', { type: 'checkbox', id: 'danger-toggle' }),
        el('span', {}, t('list.danger.toggle'))
      )
    ),

    el(
      'div',
      { class: 'list-categories container', id: 'list-categories' },
      el('button', { class: 'category-pill is-active', 'data-category': '' }, t('category.all')),
      ...CATEGORIES.map(cat =>
        el('button', { class: 'category-pill', 'data-category': cat }, t(`category.${cat}`))
      )
    ),

    el(
      'section',
      { class: 'list-grid-wrap container-wide' },
      el('div', { class: 'list-status', id: 'list-status' }, t('common.loading')),
      el('div', { class: 'list-grid', id: 'list-grid' })
    )
  );

  root.append(renderHeader(), view, renderFooter());

  // ---------- Logique ----------
  const grid = view.querySelector('#list-grid');
  const status = view.querySelector('#list-status');
  const searchInput = view.querySelector('#search-input');
  const dangerToggle = view.querySelector('#danger-toggle');
  const categoriesBar = view.querySelector('#list-categories');

  let allSpecies = [];

  async function refresh() {
    status.textContent = t('common.loading');
    grid.innerHTML = '';
    try {
      allSpecies = await listSpecies({
        search: searchInput.value,
        dangerOnly: dangerToggle.checked,
        zoneId,
        category: selectedCategory,
      });
      renderGrid();
    } catch (err) {
      status.textContent = t('common.loadingError');
      console.error(err);
    }
  }

  categoriesBar.addEventListener('click', e => {
    const pill = e.target.closest('.category-pill');
    if (!pill) return;
    categoriesBar.querySelectorAll('.category-pill').forEach(p => p.classList.remove('is-active'));
    pill.classList.add('is-active');
    selectedCategory = pill.dataset.category || null;
    refresh();
  });

  function renderGrid() {
    grid.innerHTML = '';
    if (allSpecies.length === 0) {
      status.textContent = t('list.empty');
      return;
    }
    status.textContent = t('list.count', { count: allSpecies.length });

    for (const sp of allSpecies) {
      const name = displayName(sp);
      const card = el(
        'a',
        { href: `#/species/${sp.id}`, class: 'species-card' },
        el(
          'div',
          { class: 'species-card-photo' },
          sp.primary_image_url
            ? el('img', { src: sp.primary_image_url, alt: name, loading: 'lazy' })
            : el('div', { class: 'species-card-photo-empty' }, '—'),
          sp.danger_level > 0 &&
            el(
              'span',
              {
                class: `species-card-danger danger-${sp.danger_level}`,
                title: t('list.danger.tooltip', { level: sp.danger_level }),
              },
              '⚠'
            ),
          sp.is_invasive &&
            el('span', { class: 'species-card-invasive' }, t('species.invasive'))
        ),
        el(
          'div',
          { class: 'species-card-body' },
          el('h3', { class: 'species-card-name' }, name),
          el('p', { class: 'species-card-sciname' }, sp.scientific_name),
          formatSize(sp.max_size_cm) &&
            el('p', { class: 'species-card-size' }, formatSize(sp.max_size_cm))
        )
      );
      grid.append(card);
    }
  }

  searchInput.addEventListener('input', debounce(refresh, 200));
  dangerToggle.addEventListener('change', refresh);

  await refresh();
}
